import { useContext, useEffect, useState } from "react";
import FileDnd from "../../../components/FileDnD";
import FormField from "../../../components/FormFiel/FormFiel";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { HouseContext } from "../../../context/HouseContext";
import { putProperty } from "../../../application/putProperty";
import { BounceLoader } from "react-spinners";
import {
  deleteImageFromStorage,
  getImagesById,
  saveMultipleImages,
} from "../../../application/saveImg";
import {
  FaCamera,
  FaHome,
  FaMapMarkerAlt,
  FaListUl,
  FaAlignLeft,
  FaMap,
  FaStar,
  FaDollarSign,
  FaImages,
  FaPencilAlt,
} from "react-icons/fa";

function SectionCard({ icon: Icon, title, children, className = "" }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden ${className}`}>
      <div className="flex items-center gap-2.5 px-5 py-3.5 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-100 dark:border-gray-700">
        <Icon className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function PropertyEdit() {
  const { id } = useParams();
  const { houses, refreshHouses } = useContext(HouseContext);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [urlImg, setUrlImg] = useState([]);
  const [fileDragging, setFileDragging] = useState(null);
  const [fileDropping, setFileDropping] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const currency = watch("currency");
  const state = watch("state");

  const handleDrop = (event) => {
    event.preventDefault();
    event.preventDefault();
    const updatedFiles = [...files];
    const [removed] = updatedFiles.splice(fileDragging, 1);
    updatedFiles.splice(fileDropping, 0, removed);

    setFiles(updatedFiles);

    const updatedUrlImg = updatedFiles.map((file) => {
      if (file instanceof File) {
        return { preview: URL.createObjectURL(file) };
      } else {
        return { preview: file.preview };
      }
    });

    setUrlImg(updatedUrlImg);
    setFileDragging(null);
    setFileDropping(null);
  };

  const handleDragEnter = (index) => setFileDropping(index);

  const handleDragStart = (index) => setFileDragging(index);

  useEffect(() => {
    const foundProperty = houses.find((house) => house.id === id);
    if (foundProperty) {
      setProperty(foundProperty);
      setValue("name", foundProperty.name);
      setValue("type", foundProperty.type);
      setValue("state", foundProperty.state);
      setValue("country", foundProperty.country);
      setValue("address", foundProperty.address);
      setValue("ambientes", foundProperty.characteristics.ambientes);
      setValue("dormitorios", foundProperty.characteristics.dormitorios);
      setValue("baños", foundProperty.characteristics.baños);
      setValue("patio", foundProperty.characteristics.patio);
      setValue("expensas", foundProperty.characteristics.expensas);
      setValue("estado", foundProperty.characteristics.estado);
      setValue("plantas", foundProperty.characteristics.plantas);
      setValue("contrato", foundProperty.characteristics.contrato);
      setValue("description", foundProperty.description);
      setValue("currency", foundProperty.currency);
      setValue("price", foundProperty.price);
      setValue("maps", foundProperty.maps);
      setValue("highlights", foundProperty.highlights);
    }
  }, [id, houses, setValue]);

  useEffect(() => {
    const foundProperty = houses.find((house) => house.id === id);
    setProperty(foundProperty);
  }, [id, houses]);

  useEffect(() => {
    if (property) {
      const fetchImages = async () => {
        try {
          const urls = await getImagesById(property.id);
          console.log(urls);
          setUrlImg(urls.map((url) => ({ preview: url })));
          setFiles(urls.map((url) => ({ preview: url })));
        } catch (error) {
          console.error("Error al obtener las imágenes:", error);
        }
      };
      fetchImages();
    }
  }, [property]);

  if (!property) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <BounceLoader color="#187795" size={50} />
          <p className="text-gray-500 dark:text-gray-400">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  const handleFiles = (newFiles) => {
    if (Array.isArray(newFiles)) {
      getImagesById(property.id)
        .then((existingImageUrls) => {
          const updatedFiles = [
            ...existingImageUrls.map((url) => ({
              preview: url,
              isFirebaseURL: true,
            })),
            ...newFiles.map((file, index) => ({
              preview: URL.createObjectURL(file),
              isFirebaseURL: false,
              file: file,
            })),
          ];
          const uniqueFiles = updatedFiles.filter(
            (file, index, self) =>
              index ===
              self.findIndex((t) =>
                t.isFirebaseURL
                  ? t.preview === file.preview
                  : t.file?.name === file.file?.name
              )
          );
          setFiles(uniqueFiles);
          setUrlImg(uniqueFiles.map((file) => ({ preview: file.preview })));
        })
        .catch((error) => {
          console.error("Error al obtener las imágenes:", error);
        });
    } else {
      console.error("handleFiles: El valor recibido no es un array");
    }
  };

  const toTitleCase = (str) =>
    str ? str.trim().replace(/\b\w/g, (c) => c.toUpperCase()) : "";

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      const newFilesToUpload = files.filter(
        (file) => !file.isFirebaseURL && file.file
      );

      const resultStorage =
        newFilesToUpload.length > 0
          ? await saveMultipleImages(
              newFilesToUpload.map((file) => file.file),
              property.id
            )
          : [];

      let allImageUrls = [];

      if (files && Array.isArray(files)) {
        files.forEach((file) => {
          if (
            file &&
            file.preview &&
            file.preview.startsWith("https://firebasestorage.googleapis.com/")
          ) {
            allImageUrls.push(file.preview);
          }
        });
      }

      if (resultStorage && Array.isArray(resultStorage)) {
        resultStorage.forEach((url) => {
          allImageUrls.push(url);
        });
      }

      const formattedData = {
        name: toTitleCase(formData.name),
        type: formData.type,
        state: formData.state,
        country: toTitleCase(formData.country),
        address: toTitleCase(formData.address),
        characteristics: {
          ambientes: formData.ambientes || "",
          dormitorios: formData.dormitorios || "",
          baños: formData.baños || "",
          patio: formData.patio || "",
          expensas: formData.expensas || "",
          estado: formData.estado || "",
          plantas: formData.plantas || "",
          contrato: formData.contrato || "",
        },
        description: formData.description,
        currency: formData.currency,
        price: formData.price || (currency === "Consultar" && ""),
        maps: formData.maps,
        highlights: formData.highlights === "true" ? true : false,
        images: allImageUrls,
      };
      await putProperty(property.idFirebase, formattedData);
      await refreshHouses();
      setError(null);
      setLoading(false);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setSuccess(false), 8000);
    } catch (err) {
      console.error("Error al guardar la propiedad:", err);
      setLoading(false);
      setError("Error al guardar los cambios. Intenta de nuevo.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setError(null), 8000);
    }
  };

  const removeFile = async (index) => {
    try {
      const fileToRemove = urlImg[index].preview;
      await deleteImageFromStorage(fileToRemove);

      const updatedUrlImg = [...urlImg];
      updatedUrlImg.splice(index, 1);
      setUrlImg(updatedUrlImg);

      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      setFiles(updatedFiles);
    } catch (error) {
      console.error("Error al eliminar la imagen:", error);
    }
  };

  return (
    <div className="px-4 md:px-6 py-4">
      {loading && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-20 flex items-center justify-center z-50">
          <div className="absolute inset-0 backdrop-blur-sm"></div>
          <div className="flex flex-col justify-center items-center z-10">
            <BounceLoader className="my-2" color="#187795" size={60} />
            <h2 className="text-xl dark:text-gray-100">Guardando Propiedad</h2>
          </div>
        </div>
      )}
        <div className="max-w-4xl mx-auto space-y-5">
          {/* Page Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <FaPencilAlt className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Editar Propiedad</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{property.name}</p>
            </div>
          </div>

          {/* Feedback */}
          {success && (
            <div className="flex items-center gap-3 p-5 bg-green-100 dark:bg-green-900/30 border-2 border-green-400 dark:border-green-700 rounded-xl shadow-md">
              <div className="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full flex-shrink-0">
                <span className="text-white text-xl font-bold">&#10003;</span>
              </div>
              <p className="font-semibold text-green-800 dark:text-green-200">Cambios guardados con exito.</p>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-3 p-5 bg-red-100 dark:bg-red-900/30 border-2 border-red-400 dark:border-red-700 rounded-xl shadow-md">
              <div className="flex items-center justify-center w-10 h-10 bg-red-500 rounded-full flex-shrink-0">
                <span className="text-white text-xl font-bold">&#10007;</span>
              </div>
              <p className="font-semibold text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Upload New Images */}
          <SectionCard icon={FaCamera} title="Subir nuevas imagenes">
            <FileDnd onFilesChange={handleFiles} />
          </SectionCard>

          {/* Published Images */}
          <SectionCard icon={FaImages} title="Imagenes publicadas">
            {urlImg.length > 0 ? (
              <div
                className="grid grid-cols-2 gap-3 md:grid-cols-5"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                {urlImg.map((file, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col items-center overflow-hidden text-center bg-gray-100 dark:bg-gray-700 border dark:border-gray-600 rounded-lg cursor-move select-none ${
                      fileDragging === index ? "border-primary ring-2 ring-primary/30" : ""
                    }`}
                    style={{ paddingTop: "100%" }}
                    draggable="true"
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                  >
                    <button
                      className="absolute top-1.5 right-1.5 z-50 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full focus:outline-none transition-colors"
                      type="button"
                      onClick={() => removeFile(index)}
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                    <img
                      className="absolute inset-0 z-0 object-cover w-full h-full"
                      src={file.preview}
                      alt={file.name}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
                No hay imagenes publicadas
              </p>
            )}
          </SectionCard>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Basic Data */}
            <SectionCard icon={FaHome} title="Datos principales">
              <div className="space-y-4">
                <FormField
                  label="Nombre"
                  id="name"
                  name="nombre"
                  type="text"
                  required={true}
                  maxLength={50}
                  placeholder="Ej: Casa en barrio norte"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Tipo"
                    id="type"
                    required={true}
                    name="tipo"
                    type="select"
                    errors={errors}
                    register={register}
                    options={[
                      { value: "", label: "Seleccionar tipo" },
                      { value: "Casa", label: "Casa" },
                      { value: "Departamento", label: "Departamento" },
                      { value: "Galpón", label: "Galpón" },
                      { value: "Local", label: "Local" },
                      { value: "Oficina", label: "Oficina" },
                      { value: "Terreno", label: "Terreno" },
                    ]}
                  />
                  <FormField
                    label="Estado"
                    id="state"
                    required={true}
                    name="estado"
                    type="select"
                    errors={errors}
                    register={register}
                    options={[
                      { value: "", label: "Seleccionar estado" },
                      { value: "Alquiler", label: "Alquiler" },
                      { value: "Alquilado", label: "Alquilado" },
                      { value: "Venta", label: "Venta" },
                      { value: "Vendido", label: "Vendido" },
                    ]}
                  />
                </div>
              </div>
            </SectionCard>

            {/* Location */}
            <SectionCard icon={FaMapMarkerAlt} title="Ubicacion">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  label="Localidad"
                  id="country"
                  name="localidad"
                  required={true}
                  type="text"
                  maxLength={20}
                  placeholder="Ej: Chascomus"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
                <FormField
                  label="Direccion"
                  id="address"
                  name="dirección"
                  required={true}
                  type="text"
                  maxLength={45}
                  placeholder="Ej: Av. San Martin 123"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
              </div>
            </SectionCard>

            {/* Characteristics */}
            <SectionCard icon={FaListUl} title="Caracteristicas">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField
                  label="Ambientes"
                  id="ambientes"
                  name="ambientes"
                  required={false}
                  type="number"
                  maxLength={10}
                  placeholder="0"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
                <FormField
                  label="Dormitorios"
                  id="dormitorios"
                  name="dormitorios"
                  required={false}
                  type="number"
                  maxLength={10}
                  placeholder="0"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
                <FormField
                  label="Banos"
                  id="baños"
                  name="baños"
                  required={false}
                  type="number"
                  maxLength={10}
                  placeholder="0"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
                <FormField
                  label="Patio"
                  id="patio"
                  required={false}
                  name="patio"
                  type="select"
                  errors={errors}
                  register={register}
                  options={[
                    { value: "", label: "Seleccionar" },
                    { value: "Si", label: "Si" },
                    { value: "No", label: "No" },
                  ]}
                />
                <FormField
                  label="Expensas"
                  id="expensas"
                  required={false}
                  name="expensas"
                  type="select"
                  errors={errors}
                  register={register}
                  options={[
                    { value: "", label: "Seleccionar" },
                    { value: "Si", label: "Si" },
                    { value: "No", label: "No" },
                  ]}
                />
                <FormField
                  label="Estado del inmueble"
                  id="estado"
                  required={false}
                  name="estado"
                  type="select"
                  errors={errors}
                  register={register}
                  options={[
                    { value: "", label: "Seleccionar" },
                    { value: "Nuevo", label: "Nuevo" },
                    { value: "Usado", label: "Usado" },
                    { value: "Bueno", label: "Bueno" },
                    { value: "Muy Bueno", label: "Muy Bueno" },
                    { value: "Exelente", label: "Exelente" },
                  ]}
                />
                <FormField
                  label="Plantas"
                  id="plantas"
                  name="plantas"
                  required={false}
                  type="number"
                  maxLength={10}
                  placeholder="0"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
                <FormField
                  label="Contrato"
                  id="contrato"
                  name="contrato"
                  required={false}
                  type="text"
                  maxLength={50}
                  placeholder="Ej: 2 anos"
                  autoComplete="off"
                  errors={errors}
                  register={register}
                />
              </div>
            </SectionCard>

            {/* Description */}
            <SectionCard icon={FaAlignLeft} title="Descripcion">
              <FormField
                label="Descripcion de la propiedad"
                id="description"
                name="descripcion"
                required={true}
                type="textarea"
                rows={6}
                placeholder="Describe la propiedad, sus comodidades, ubicacion, etc..."
                errors={errors}
                register={register}
              />
            </SectionCard>

            {/* Google Maps */}
            <SectionCard icon={FaMap} title="Mapa">
              <FormField
                label="URL de Google Maps"
                id="maps"
                name="mapa"
                required={true}
                type="text"
                placeholder="https://www.google.com/maps?q=..."
                autoComplete="off"
                errors={errors}
                register={register}
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Pega el link de Google Maps para mostrar la ubicacion en el mapa
              </p>
            </SectionCard>

            {/* Highlight */}
            <SectionCard icon={FaStar} title="Destacar propiedad">
              <FormField
                label="Destacar"
                id="highlights"
                required={false}
                name="destacar propiedad"
                type="select"
                errors={errors}
                register={register}
                options={[
                  { value: "", label: "Seleccionar" },
                  { value: true, label: "Si" },
                  { value: false, label: "No" },
                ]}
              />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Las propiedades destacadas aparecen primero en la lista de resultados.
              </p>
            </SectionCard>

            {/* Price */}
            {state !== "Alquilado" && state !== "Vendido" && (
              <SectionCard icon={FaDollarSign} title="Precio">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label="Moneda"
                    id="currency"
                    required={true}
                    name="moneda"
                    type="select"
                    errors={errors}
                    register={register}
                    options={[
                      { value: "", label: "Seleccionar moneda" },
                      { value: "$", label: "$ (Pesos)" },
                      { value: "USD", label: "USD (Dolares)" },
                      { value: "Consultar", label: "Consultar" },
                    ]}
                  />
                  {currency !== "Consultar" && (
                    <FormField
                      label="Precio"
                      id="price"
                      name="precio"
                      required={true}
                      type="number"
                      maxLength={10}
                      placeholder="0"
                      autoComplete="off"
                      errors={errors}
                      register={register}
                    />
                  )}
                </div>
              </SectionCard>
            )}

            {/* Spacer for sticky button */}
            <div className="h-20"></div>

            {/* Submit - sticky bottom */}
            <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3 z-20">
              <div className="max-w-4xl mx-auto flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 text-white font-semibold bg-primary hover:bg-secondary rounded-lg transition-colors shadow-sm disabled:opacity-50"
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </form>
        </div>
    </div>
  );
}

export default PropertyEdit;
